import { useEffect, useMemo, useRef, useState } from 'react';
import { CandlestickSeries, ColorType, HistogramSeries, createChart, createSeriesMarkers, type IChartApi, type ISeriesApi, type ISeriesMarkersPluginApi, type MouseEventParams, type SeriesMarker, type Time, type UTCTimestamp } from 'lightweight-charts';
import type { Candle } from '../lib/api';
import { groupWalletChartTrades, prepareChartCandles, type TradeSummary, type WalletTradeGroup } from '../lib/chart-trades';
import { amount, compact, short, usd } from '../lib/format';
import type { DisplayTrade } from './Trades';
import { Empty } from './common';
import './chart-trades.css';

interface ChartProps {
  candles: Candle[];
  multiplier: number;
  symbol: string;
  volumeMultiplier?: number;
  wallet?: string;
  trades?: DisplayTrade[];
  intervalSeconds?: 60 | 3600;
}
const NO_TRADES: DisplayTrade[] = [];
function tokenSize(tokens: bigint, symbol: string) {
  const value = Number(tokens) / 1e8;
  return `${Number.isFinite(value) ? value > 0 && value < 0.01 ? '<0.01' : compact(value) : amount(tokens, 8, 2)} ${symbol}`;
}
function compactUsd(value: number) { return value > 0 && value < 0.01 ? '<$0.01' : value === 0 ? '$0' : `$${compact(value)}`; }
function summarySize(summary: TradeSummary, symbol: string) {
  return summary.unknownUsdCount ? tokenSize(summary.tokens, symbol) : summary.knownUsd > 0 && summary.knownUsd < 0.01 ? '<$0.01' : usd(summary.knownUsd);
}
function markerLabel(group: WalletTradeGroup, symbol: string) {
  const size = group.unknownUsdCount ? tokenSize(group.tokens, symbol.slice(0, 8)) : compactUsd(group.knownUsd);
  return `${group.buy ? 'B' : 'S'}${group.count > 1 ? `×${group.count}` : ''} ${size}`;
}
function exactUtc(at: bigint) {
  const seconds = at / 1_000_000_000n;
  const date = new Date(Number(seconds * 1000n));
  if (!Number.isFinite(date.getTime())) return 'Timestamp unavailable';
  const fraction = (at % 1_000_000_000n).toString().padStart(9, '0').replace(/0+$/, '');
  return `${date.toISOString().slice(0, 19).replace('T', ' ')}${fraction ? `.${fraction}` : ''} UTC`;
}
function exactUsd(value: number | null) {
  return value !== null && Number.isFinite(value) && value >= 0 ? `$${value.toLocaleString('en-US', { maximumSignificantDigits: 15 })}` : 'USD unavailable';
}
const candleTime = (time: number) => new Date(time * 1000).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';

export function Chart({ candles, multiplier, symbol, volumeMultiplier = 1, wallet = '', trades = NO_TRADES, intervalSeconds = 3600 }: ChartProps) {
  const container = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const previousData = useRef<{ times: number[]; interval: number; symbol: string } | null>(null);
  const groupsRef = useRef(new Map<number, WalletTradeGroup[]>());
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const valid = useMemo(() => prepareChartCandles(candles, multiplier, volumeMultiplier), [candles, multiplier, volumeMultiplier]);
  const annotations = useMemo(() => groupWalletChartTrades(valid, trades, wallet, intervalSeconds), [valid, trades, wallet, intervalSeconds]);
  const groupsByTime = useMemo(() => {
    const result = new Map<number, WalletTradeGroup[]>();
    for (const group of annotations.groups) result.set(group.time, [...(result.get(group.time) ?? []), group]);
    return result;
  }, [annotations]);
  groupsRef.current = groupsByTime;
  const selectedGroups = selectedTime === null ? undefined : groupsByTime.get(selectedTime);

  // A single chart survives polling and wallet/size/side filter changes.
  useEffect(() => {
    if (!container.current) return;
    const chart = createChart(container.current, {
      autoSize: true, height: container.current.clientHeight || 450,
      layout: { background: { type: ColorType.Solid, color: '#10141a' }, textColor: '#7d8999', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, attributionLogo: true },
      grid: { vertLines: { color: '#1b222b' }, horzLines: { color: '#1b222b' } },
      rightPriceScale: { borderColor: '#252c36', scaleMargins: { top: 0.14, bottom: 0.24 } },
      timeScale: { borderColor: '#252c36', timeVisible: true, secondsVisible: false, rightOffset: 5 },
      crosshair: { vertLine: { color: '#66725f', labelBackgroundColor: '#333d2a' }, horzLine: { color: '#66725f', labelBackgroundColor: '#333d2a' } },
    });
    const series = chart.addSeries(CandlestickSeries, { upColor: '#a7db70', downColor: '#ee737f', borderVisible: false, wickUpColor: '#a7db70', wickDownColor: '#ee737f', priceFormat: { type: 'price', precision: 10, minMove: 0.0000000001 } });
    const volume = chart.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'volume', lastValueVisible: false, priceLineVisible: false });
    volume.priceScale().applyOptions({ scaleMargins: { top: 0.84, bottom: 0 }, visible: false });
    const markers = createSeriesMarkers(series, [], { autoScale: true });
    chartRef.current = chart; seriesRef.current = series; volumeRef.current = volume; markersRef.current = markers;
    const selectCandle = (event: MouseEventParams<Time>) => {
      if (typeof event.time === 'number' && groupsRef.current.has(event.time)) setSelectedTime(event.time);
    };
    chart.subscribeCrosshairMove(selectCandle);
    chart.subscribeClick(selectCandle);
    return () => {
      chart.unsubscribeCrosshairMove(selectCandle); chart.unsubscribeClick(selectCandle);
      markers.detach(); chart.remove();
      chartRef.current = null; seriesRef.current = null; volumeRef.current = null; markersRef.current = null; previousData.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current, series = seriesRef.current, volume = volumeRef.current;
    if (!chart || !series || !volume) return;
    const previous = previousData.current;
    const range = chart.timeScale().getVisibleLogicalRange();
    series.setData(valid.map(candle => ({ time: candle.time as UTCTimestamp, open: candle.open, high: candle.high, low: candle.low, close: candle.close })));
    volume.setData(valid.map(candle => ({ time: candle.time as UTCTimestamp, value: candle.volume, color: candle.rising ? '#a7db702b' : '#ee737f2b' })));
    const times = valid.map(candle => candle.time);
    const fit = () => {
      // Fit the data directly: fitContent() is deferred until the next draw.
      const width = chart.timeScale().width() || container.current?.clientWidth || 320;
      const padding = Math.min(56, width / 4), from = -0.5, to = valid.length + 4.5;
      const extraBars = (to - from) * padding / (width - padding);
      chart.timeScale().setVisibleLogicalRange({ from: from - extraBars, to });
    };
    if (valid.length) {
      if (!previous?.times.length || previous.interval !== intervalSeconds || previous.symbol !== symbol || !range) fit();
      else {
        // Preserve the viewed bars when polling appends history or rolls old candles off.
        const newIndexes = new Map(times.map((time, index) => [time, index]));
        const oldIndex = previous.times.findIndex(time => newIndexes.has(time));
        if (oldIndex < 0) fit();
        else {
          const shift = newIndexes.get(previous.times[oldIndex])! - oldIndex;
          chart.timeScale().setVisibleLogicalRange({ from: Number(range.from) + shift, to: Number(range.to) + shift });
        }
      }
    }
    previousData.current = { times, interval: intervalSeconds, symbol };
  }, [valid, intervalSeconds, symbol]);

  useEffect(() => {
    const markers: SeriesMarker<Time>[] = annotations.groups.map(group => ({
      id: group.id, time: group.time as UTCTimestamp, position: group.buy ? 'belowBar' : 'aboveBar', shape: group.buy ? 'arrowUp' : 'arrowDown',
      color: group.buy ? '#a7db70' : '#ee737f', text: markerLabel(group, symbol), size: 1,
    }));
    markersRef.current?.setMarkers(markers);
  }, [annotations, symbol]);
  useEffect(() => { setSelectedTime(null); }, [wallet, intervalSeconds, symbol]);

  return <div className="wallet-chart">
    {wallet && <div className="chart-wallet-legend" aria-label="Wallet chart annotations">
      <div className="chart-wallet-heading"><strong>Wallet trades</strong><span className="mono" title={wallet}>{short(wallet, 6)}</span></div>
      <div className="chart-wallet-totals"><span className="green">↑ {annotations.buy.count} {annotations.buy.count === 1 ? 'buy' : 'buys'} · {summarySize(annotations.buy, symbol)}</span><span className="red">↓ {annotations.sell.count} {annotations.sell.count === 1 ? 'sell' : 'sells'} · {summarySize(annotations.sell, symbol)}</span></div>
      <p>{annotations.matchedTradeCount ? `${annotations.matchedTradeCount} filtered ${annotations.matchedTradeCount === 1 ? 'trade marked' : 'trades marked'} in loaded candles. Hover or tap a marked candle for trade sizes.` : valid.length ? 'No filtered wallet trades match the loaded candles.' : 'Wallet markers will appear when candles are available.'}{annotations.excludedTradeCount > 0 && ` ${annotations.excludedTradeCount} filtered ${annotations.excludedTradeCount === 1 ? 'trade is' : 'trades are'} outside this candle history or in a gap.`}</p>
    </div>}
    <div className="chart-area" aria-label={`${symbol} candlestick price chart${wallet ? ' with selected wallet buys and sells' : ''}`}>
      <div ref={container} className="chart-canvas" style={{ visibility: valid.length ? 'visible' : 'hidden' }} aria-hidden={!valid.length} />
      {!valid.length && <div className="chart-empty"><Empty title="No candles yet"><p>Trading candles will appear when this market records activity.</p></Empty></div>}
    </div>
    {wallet && annotations.matchedTradeCount > 0 && <div className="chart-wallet-inspector">
      {selectedGroups && <section className="chart-wallet-selected" aria-label="Selected candle wallet trades">
        <div className="chart-wallet-detail-heading"><strong>{candleTime(selectedTime!)} · {intervalSeconds === 60 ? '1m' : '1h'} candle</strong><button type="button" aria-label="Close candle trade details" onClick={() => setSelectedTime(null)}>×</button></div>
        <ul className="chart-wallet-trade-list">{selectedGroups.flatMap(group => group.trades).sort((a, b) => a.at < b.at ? -1 : a.at > b.at ? 1 : a.id.localeCompare(b.id)).map(trade => <li key={`${trade.market}:${trade.id}`}>
          <span className={trade.buy ? 'green' : 'red'}>{trade.buy ? '↑ Buy' : '↓ Sell'}</span><strong>{exactUsd(trade.usd)}</strong>
          <span>{amount(trade.tokens, 8, 8)} {symbol}</span><span>{trade.base} {trade.baseSymbol}</span><time dateTime={new Date(Number(trade.at / 1_000_000n)).toISOString()}>{exactUtc(trade.at)}</time>
        </li>)}</ul>
      </section>}
      <details className="chart-wallet-all-trades"><summary>Browse marked trades ({annotations.matchedTradeCount})</summary><ul>{annotations.groups.map(group => <li key={group.id}><button type="button" onClick={() => setSelectedTime(group.time)} className={group.buy ? 'green' : 'red'} aria-label={`Inspect ${group.count} ${group.buy ? 'buy' : 'sell'} ${group.count === 1 ? 'trade' : 'trades'} at ${candleTime(group.time)}`}><time dateTime={new Date(group.time * 1000).toISOString()}>{candleTime(group.time)}</time><span>{group.buy ? '↑ Buy' : '↓ Sell'}{group.count > 1 ? ` ×${group.count}` : ''}</span><strong>{summarySize(group, symbol)}</strong></button></li>)}</ul></details>
      <p className="chart-wallet-method">Arrows mark the candle containing the trade, not its execution price. Buys and sells are grouped separately per candle. USD sizes match the trade table; groups with unavailable USD show token totals.</p>
    </div>}
  </div>;
}
